// Laser remnant / stock-drop label confirmation (2026-09-23).
//
// Remnants were leaving the laser unlabeled with "the label never printed" as the
// explanation. Every print job that puts a remnant or drop label on the ZP 505 now asks
// the operator whether it printed (RemnantLabelConfirmModal). A "didn't print" makes
// jobq-api post the label PDF to shop alerts so the office can reprint it, next to what
// the print agent itself reported for that job.
//
// Pure helpers only: what the question says and what the live print status reads as.
// The job objects are jobq-api's print-job view (GET /print/queue/{id},
// GET /tasks/{id}/label_confirmations) or the `label_confirmation` that Add Sheet and
// POST /print/queue return: { print_job_id, sheet_index, labels }.

const inches = (x) => (x === null || x === undefined || x === '' ? '?"' : `${Number(x)}"`);

// 'Remnant 24" × 48" ×1 · Drop 48" × 48" ×2' (per sheet; a reprint says it covers every sheet).
export function labelSummaryText(labels) {
  const l = labels || {};
  const bits = [];
  (l.remnants || []).forEach((r) => bits.push(`Remnant ${inches(r.width)} × ${inches(r.length)} ×${r.quantity}`));
  (l.drops || []).forEach((d) => bits.push(`Drop ${inches(d.width)} × ${inches(d.length)} ×${d.quantity}`));
  let text = bits.length ? bits.join(' · ') : 'remnant / drop labels';
  if (l.sheets > 1) text += ` per sheet, all ${l.sheets} sheets`;
  return text;
}

// What the label goes on, for the operator's instruction: "the remnant", "the drop",
// "the remnant and the drop".
export function pieceText(labels) {
  const l = labels || {};
  const rem = (l.remnants || []).length > 0;
  const drop = (l.drops || []).length > 0;
  if (rem && drop) return 'the remnant and the drop';
  if (drop) return (l.label_count || 0) > 1 ? 'the drops' : 'the drop';
  return (l.label_count || 0) > 1 ? 'the remnants' : 'the remnant';
}

// "Remnant label", "Stock drop labels", "Remnant + drop labels".
export function labelNoun(labels) {
  const l = labels || {};
  const rem = (l.remnants || []).length > 0;
  const drop = (l.drops || []).length > 0;
  const many = (l.label_count || 0) > 1;
  if (rem && drop) return 'Remnant + drop labels';
  if (drop) return many ? 'Stock drop labels' : 'Stock drop label';
  return many ? 'Remnant labels' : 'Remnant label';
}

// The question's title: "Remnant label — sheet 3" / "Remnant labels — reprint".
export function confirmationTitle(conf) {
  const noun = labelNoun(conf && conf.labels);
  if (conf && conf.sheet_index) return `${noun} — sheet ${conf.sheet_index}`;
  return `${noun} — reprint`;
}

export function clockText(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' });
}

// How long a label may sit un-picked-up before the popup says the Pi may be down.
// The agent polls every 4 s and a label prints in ~2 s.
export const PICKUP_WARN_SECONDS = 20;

// The live printer line in the popup: { tone: 'waiting'|'ok'|'warn'|'error', text }.
// `job` is the polled print-job view (null before the first poll lands).
export function printStatusLine(job) {
  if (!job) return { tone: 'waiting', text: 'Sending the label to the printer…' };
  const age = Number(job.seconds_since_requested) || 0;
  const retries = Number(job.retries) || 0;
  switch (job.status) {
    case 'printed':
      return { tone: 'ok', text: `The printer finished this label at ${clockText(job.printed_at)}.` };
    case 'failed':
      return {
        tone: 'error',
        text: 'The printer reported a problem and gave up on this label'
          + (job.failure_reason ? `: ${shortReason(job.failure_reason)}` : '.'),
      };
    case 'cancelled':
      return { tone: 'error', text: 'This print job was cancelled.' };
    case 'claimed':
      return { tone: 'waiting', text: 'Printing…' };
    case 'pending':
      if (retries > 0) {
        return { tone: 'warn', text: `The printer had a problem and is trying again (try ${retries + 1} of ${job.max_retries || 3}).` };
      }
      if (age >= PICKUP_WARN_SECONDS) {
        return { tone: 'warn', text: `The label printer hasn't picked this up yet (${Math.round(age)} s). The laser Pi may be offline.` };
      }
      return { tone: 'waiting', text: 'Waiting for the label printer…' };
    default:
      return { tone: 'waiting', text: `Printer status: ${job.status}` };
  }
}

function shortReason(reason) {
  const r = String(reason).replace(/^RuntimeError:\s*/, '');
  return r.length > 120 ? `${r.slice(0, 117)}…` : r;
}

// Keep polling while the print agent still has the job.
export function isPrintSettled(job) {
  return !!job && ['printed', 'failed', 'cancelled'].includes(job.status);
}

// "It didn't print" on a label the agent says it printed gets one more look first:
// most "didn't print" labels are sitting in the tray or on the floor.
export function needsDoubleCheck(job) {
  return !!job && job.status === 'printed';
}

// What the popup says after a "didn't print" answer: { tone, text }.
export function officeOutcome(resp, conf) {
  const piece = pieceText(conf && conf.labels);
  const which = conf && conf.sheet_index ? ` for sheet ${conf.sheet_index}` : '';
  if (resp && resp.office_alerted) {
    return {
      tone: 'ok',
      text: `The office has the label in Slack (shop alerts). They will print it and bring it out. `
        + `Set ${piece} aside until it is labeled.`,
    };
  }
  return {
    tone: 'error',
    text: `Slack could not be reached, so the office was NOT told. Tell the office in person that the `
      + `label${which} didn't print, and set ${piece} aside until it is labeled.`,
  };
}

// A POST that died in transit (gateway timeout, Wi-Fi) may still have landed. The popup
// re-reads the job and treats an answer on record as landed: { landed, answer, office_alerted }.
export function answerOnRecord(job) {
  if (!job || !job.label_confirmation) return { landed: false };
  return {
    landed: true,
    answer: job.label_confirmation,
    office_alerted: !!job.office_alerted_at,
  };
}
